import { doc, getDoc, arrayUnion, arrayRemove, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';
import { sendEmail } from './endpoints/SendEmail';
import getServerTimestamp from './endpoints/GetServerTimestamp';
import { processScanPayment } from './endpoints/ProcessScanPayment';
import { processScanRefund } from './endpoints/ProcessScanRefund';
import CostContext from '../contexts/CostContext';
import dayjs from 'dayjs';

export const handleScanAction = async ({
  action,
  clientId,
  scanId,
  scan,
  currentUser,
  cardId,
  practitionerId,
  trackings = null,
}) => {
  const timestamp = await getServerTimestamp();
  const scanRef = doc(db, 'clients', clientId, 'scans', scanId);
  const clientRef = doc(db, 'clients', clientId);
  let updatedScan = { ...scan };
  let transId;
  let amount;

  await runTransaction(db, async (transaction) => {
    const scanSnap = await getDoc(scanRef);
    if (!scanSnap.exists()) {
      throw new Error('Scan document not found');
    }
    const scanData = scanSnap.data();
    const clientSnap = await getDoc(clientRef);
    if (!clientSnap.exists()) {
      throw new Error('Client document not found');
    }
    const clientData = clientSnap.data();
    const transactions = scanData.transactions || [];

    const activeScansRef = doc(db, 'app', 'activeScans');
    const activeScansSnap = await getDoc(activeScansRef);
    const activeScansData = activeScansSnap.exists() ? activeScansSnap.data() : { scans: [] };
    const practitionerActiveScansRef = doc(db, 'activeScans', practitionerId);
    const practitionerActiveScansSnap = await getDoc(practitionerActiveScansRef);
    const practitionerActiveScansData = practitionerActiveScansSnap.exists()
      ? practitionerActiveScansSnap.data()
      : { scans: [] };

    if (action === 'scanPaid') {
      let transType;
      let card = null;

      const isFree = scanData.isFreeScan === true;

      if (!isFree) {
        const practitionerRef = doc(db, 'practitioners', practitionerId);
        const practitionerSnap = await getDoc(practitionerRef);
        if (!practitionerSnap.exists() || !practitionerSnap.data().squareId) {
          throw new Error('Cannot process payment, missing customer record in payment system.');
        }
        const squareId = practitionerSnap.data().squareId;

        let paymentCardId = scanData.cardId || cardId;
        if (!paymentCardId) {
          throw new Error('NO_CARD_ON_FILE: No card selected for payment.');
        }

        const billingRef = doc(db, 'billing', practitionerId);
        const billingSnap = await getDoc(billingRef);
        if (!billingSnap.exists() || !billingSnap.data().cards) {
          throw new Error('NO_BILLING_DOCUMENT: No billing information found.');
        }
        const cards = billingSnap.data().cards;
        const selectedCard = cards.find(c => c.cardId === paymentCardId && c.enabled);
        if (!selectedCard) {
          throw new Error('INVALID_CARD: The selected card is invalid or disabled.');
        }

        const currentDate = dayjs();
        const currentMonth = currentDate.month() + 1;
        const currentYear = currentDate.year();
        if (
          currentYear > selectedCard.expYear ||
          (currentYear === selectedCard.expYear && currentMonth > selectedCard.expMonth)
        ) {
          throw new Error('CARD_EXPIRED: The selected card has expired.');
        }

        let balanceAmount;

        const clientIsImmediateFamily = clientData.immediateFamily === true;
        const hasMultiHomeDiscount = scanData.multiHomeDiscount === true;
        const outgoingTracking = scanData.kitMailed?.outgoingUSPS;

        if (clientIsImmediateFamily && hasMultiHomeDiscount && outgoingTracking) {
          const hasPaidSiblingScan = practitionerActiveScansData.scans.some(s => {
            return (
              s.id !== scanId &&
              s.scanStatus >= 4 &&
              s.outgoingTracking === outgoingTracking
            );
          });

          balanceAmount = hasPaidSiblingScan ? CostContext.ScanBalanceFamilyMultiple : CostContext.ScanBalanceFamily;
        }
        // Single family scan
        else if (clientIsImmediateFamily) {
          balanceAmount = CostContext.ScanBalanceFamily;
        }
        // Regular multi-home
        else if (hasMultiHomeDiscount) {
          balanceAmount = CostContext.ScanBalanceMultiple;
        }
        // Regular follow-up or initial
        else {
          const hasPriorCompleteScan = clientData.scans.some(
            s => s.id !== scanId && s.isComplete === true && !s.cancelled
          );
          if (hasPriorCompleteScan) {
            balanceAmount = CostContext.ScanBalanceFollowUp;
            transaction.update(scanRef, { followUpDiscount: true });
          } else {
            balanceAmount = CostContext.ScanBalanceInitial;
          }
        }

        const paymentResult = await processScanPayment({
          squareId,
          type: 'scan-final',
          cardId: paymentCardId,
          amount: balanceAmount,
        });

        transId = paymentResult.transactionId;
        card = paymentResult.card;
        transType = 'scan-final';
        amount = balanceAmount;

        const theYear = dayjs(timestamp.toDate()).year().toString();
        const practitionerTransRef = doc(db, `practitioners/${practitionerId}/transactions`, theYear);
        const transEntry = {
          id: transId,
          type: transType,
          dateCreated: timestamp,
          scanId,
          clientId,
          amount,
          card,
        };
        transaction.set(practitionerTransRef, {
          trans: arrayUnion(transEntry),
        }, { merge: true });

        transaction.update(scanRef, {
          scanPaid: { datePaid: timestamp, transId, userId: currentUser.uid },
          dateModified: timestamp,
        });
      } else {
        transId = 'free-scan-final';
        transType = 'free-scan-final';
        amount = 0;
        transaction.update(scanRef, {
          scanPaid: { datePaid: timestamp, transId, userId: currentUser.uid },
          dateModified: timestamp,
        });
      }

      transactions.push({
        transId,
        amount,
        date: timestamp,
        type: 'balance',
      });
      transaction.update(scanRef, { transactions });

      const updatedScans = activeScansData.scans.map((s) => {
        if (s.id === scanId) {
          return { ...s, scanStatus: 4, modifiedDate: timestamp, commentsToRead: s.commentsToRead || false };
        }
        return s;
      });
      transaction.set(activeScansRef, { scans: updatedScans }, { merge: true });

      const updatedPractitionerScans = practitionerActiveScansData.scans.map((s) => {
        if (s.id === scanId) {
          return { ...s, scanStatus: 4, modifiedDate: timestamp, commentsToRead: s.commentsToRead || false };
        }
        return s;
      });
      transaction.set(practitionerActiveScansRef, { scans: updatedPractitionerScans }, { merge: true });

      updatedScan = {
        ...updatedScan,
        scanPaid: { datePaid: timestamp, transId, userId: currentUser.uid },
        transactions,
        dateModified: timestamp,
      };
    } else if (action === 'extraImprintCharged') {
      const isFree = scanData.isFreeScan === true;

      if (isFree) {
        throw new Error('Cannot charge extra on free scan.');
      }

      const practitionerRef = doc(db, 'practitioners', practitionerId);
      const practitionerSnap = await getDoc(practitionerRef);
      if (!practitionerSnap.exists() || !practitionerSnap.data().squareId) {
        throw new Error('Cannot process payment, missing customer record in payment system.');
      }
      const squareId = practitionerSnap.data().squareId;

      let paymentCardId = scanData.cardId || cardId;
      if (!paymentCardId) {
        throw new Error('NO_CARD_ON_FILE: No card selected for payment.');
      }

      const billingRef = doc(db, 'billing', practitionerId);
      const billingSnap = await getDoc(billingRef);
      if (!billingSnap.exists() || !billingSnap.data().cards) {
        throw new Error('NO_BILLING_DOCUMENT: No billing information found.');
      }
      const cards = billingSnap.data().cards;
      const selectedCard = cards.find(c => c.cardId === paymentCardId && c.enabled);
      if (!selectedCard) {
        throw new Error('INVALID_CARD: The selected card is invalid or disabled.');
      }

      const currentDate = dayjs();
      const currentMonth = currentDate.month() + 1;
      const currentYear = currentDate.year();
      if (
        currentYear > selectedCard.expYear ||
        (currentYear === selectedCard.expYear && currentMonth > selectedCard.expMonth)
      ) {
        throw new Error('CARD_EXPIRED: The selected card has expired.');
      }

      const extraAmount = CostContext.ImprintRemedy;
      const paymentResult = await processScanPayment({
        squareId,
        type: 'extra-imprint',
        cardId: paymentCardId,
        amount: extraAmount,
      });

      transId = paymentResult.transactionId;
      const card = paymentResult.card;
      amount = extraAmount;

      const theYear = dayjs(timestamp.toDate()).year().toString();
      const practitionerTransRef = doc(db, `practitioners/${practitionerId}/transactions`, theYear);
      const transEntry = {
        id: transId,
        type: 'extra-imprint',
        dateCreated: timestamp,
        scanId,
        clientId,
        amount,
        card,
      };
      transaction.set(practitionerTransRef, {
        trans: arrayUnion(transEntry),
      }, { merge: true });

      // Update scan
      transaction.update(scanRef, {
        chargedExtraImprint: true,
        dateModified: timestamp,
      });

      // Push to transactions array
      transactions.push({
        transId,
        amount,
        date: timestamp,
        type: 'extra-imprint',
      });
      transaction.update(scanRef, { transactions });

      // Update active scans status if needed (optional, as not changing main status)
      const updatedScans = activeScansData.scans.map((s) => {
        if (s.id === scanId) {
          return { ...s, modifiedDate: timestamp };
        }
        return s;
      });
      transaction.set(activeScansRef, { scans: updatedScans }, { merge: true });

      const updatedPractitionerScans = practitionerActiveScansData.scans.map((s) => {
        if (s.id === scanId) {
          return { ...s, modifiedDate: timestamp };
        }
        return s;
      });
      transaction.set(practitionerActiveScansRef, { scans: updatedPractitionerScans }, { merge: true });

      updatedScan = {
        ...updatedScan,
        chargedExtraImprint: true,
        transactions,
        dateModified: timestamp,
      };
    } else if (action === 'cancel') {
      let refundTransactionId = null;
      let refundCard = null;

      if (!scanData.kitMailed && scanData.kitPaid && scanData.kitPaid.transId && !scanData.kitPaid.transId.startsWith('free-')) {
        const practitionerRef = doc(db, 'practitioners', practitionerId);
        const practitionerSnap = await getDoc(practitionerRef);
        if (!practitionerSnap.exists() || !practitionerSnap.data().squareId) {
          throw new Error('Cannot process refund, missing customer record in payment system.');
        }
        const squareId = practitionerSnap.data().squareId;
        const refundResult = await processScanRefund({
          transactionId: scanData.kitPaid.transId,
          amount: CostContext.ScanDownAmount,
          reason: 'Scan cancelled',
          customerId: squareId,
        });
        refundTransactionId = refundResult.transactionId;
        refundCard = refundResult.card;

        const currentYear = dayjs(timestamp.toDate()).year().toString();
        const practitionerTransRef = doc(db, `practitioners/${practitionerId}/transactions`, currentYear);
        transaction.set(practitionerTransRef, {
          trans: arrayUnion({
            id: refundTransactionId,
            type: 'scan-down-refund',
            dateCreated: timestamp,
            card: refundCard,
            scanId,
            clientId,
            amount: CostContext.ScanDownAmount,
          }),
        }, { merge: true });
      }

      transactions.push({
        transId: refundTransactionId,
        amount: -CostContext.ScanDownAmount,
        date: timestamp,
        type: 'scan-down-refund',
      });

      transaction.update(scanRef, {
        cancelled: {
          dateCancelled: timestamp,
          userId: currentUser.uid,
          ...(refundTransactionId && { transId: refundTransactionId }),
        },
        transactions,
        dateModified: timestamp,
      });

      const updatedScans = clientData.scans.map((s) => {
        if (s.id === scanId) {
          return { ...s, isCancelled: true };
        }
        return s;
      });
      transaction.update(clientRef, { scans: updatedScans });

      const monthKey = dayjs(scanData.scanDate).format('YYYYMM');
      const dayKey = dayjs(scanData.scanDate).format('DD');
      const scheduleDocRef = doc(db, 'schedule', monthKey);
      const scheduleDocSnap = await getDoc(scheduleDocRef);
      if (scheduleDocSnap.exists()) {
        const monthData = scheduleDocSnap.data();
        const dayData = monthData.days[dayKey] || {};
        const updatedScheduled = (dayData.scheduled || []).filter((s) => s.scanId !== scanId);
        const updatedDayData = {
          ...dayData,
          scheduled: updatedScheduled,
          scansBooked: (dayData.scansBooked || 0) - 1, // Single scan cancellation
        };
        const updatedMonthData = {
          ...monthData,
          days: {
            ...monthData.days,
            [dayKey]: updatedDayData,
          },
        };
        transaction.update(scheduleDocRef, updatedMonthData);
      }

      const scanToRemove = activeScansData.scans.find((s) => s.id === scanId);
      if (scanToRemove) {
        transaction.update(activeScansRef, {
          scans: arrayRemove(scanToRemove),
        });
      }

      const practitionerScanToRemove = practitionerActiveScansData.scans.find((s) => s.id === scanId);
      if (practitionerScanToRemove) {
        transaction.update(practitionerActiveScansRef, {
          scans: arrayRemove(practitionerScanToRemove),
        });
      }

      updatedScan = {
        ...updatedScan,
        cancelled: {
          dateCancelled: timestamp,
          userId: currentUser.uid,
          ...(refundTransactionId && { transId: refundTransactionId }),
        },
        dateModified: timestamp,
        transactions
      };
    } else {
      const mailedData = action.includes('Mailed')
        ? { dateMailed: timestamp, userId: currentUser.uid, ...(trackings || {}) }
        : { dateUploaded: timestamp, userId: currentUser.uid };
      const updates = {
        [action]: mailedData,
        dateModified: timestamp,
      };
      transaction.update(scanRef, updates);

      let newStatus = action === 'kitMailed' ? 2 : action === 'reportsUploaded' ? 5 : action === 'imprintMailed' ? 6 : scanData.scanStatus || 1;

      const updatedScanData = {
        ...scanData,
        [action]: mailedData,
      };
      let scanToMove = null;

      if (updatedScanData.reportsUploaded && updatedScanData.imprintMailed) {
        newStatus = 7; // Completed
        const updatedClientScans = clientData.scans.map((s) => {
          if (s.id === scanId) {
            return { ...s, isComplete: true };
          }
          return s;
        });
        transaction.update(clientRef, { scans: updatedClientScans });

        scanToMove = activeScansData.scans.find((s) => s.id === scanId);
        if (scanToMove) {
          const year = dayjs(timestamp.toDate()).format('YYYY');
          const pastScansRef = doc(db, 'pastScans', year);
          const pastScansSnap = await getDoc(pastScansRef);
          const pastScansData = pastScansSnap.exists() ? pastScansSnap.data() : { scans: [] };

          const pastScanEntry = {
            clientId: scanToMove.clientId,
            date: scanToMove.date,
            dateCompleted: timestamp,
            practitionerId: scanToMove.practitionerId,
            scanId: scanToMove.id,
          };

          transaction.set(pastScansRef, {
            scans: [...pastScansData.scans, pastScanEntry],
          }, { merge: true });

          const updatedScans = activeScansData.scans.map((s) => {
            if (s.id === scanId) {
              if (s.commentsToRead) {
                const { scanStatus, ...rest } = s;
                return { ...rest, modifiedDate: timestamp };
              }
              return s;
            }
            return s;
          });
          if (scanToMove && !scanToMove.commentsToRead) {
            transaction.update(activeScansRef, {
              scans: arrayRemove(scanToMove),
            });
          } else {
            transaction.set(activeScansRef, { scans: updatedScans }, { merge: true });
          }

          const practitionerScanToUpdate = practitionerActiveScansData.scans.find((s) => s.id === scanId);
          if (practitionerScanToUpdate) {
            const updatedPractitionerScans = practitionerActiveScansData.scans.map((s) => {
              if (s.id === scanId) {
                if (s.commentsToRead) {
                  const { scanStatus, ...rest } = s;
                  return { ...rest, modifiedDate: timestamp };
                }
                return s;
              }
              return s;
            });
            if (practitionerScanToUpdate.commentsToRead) {
              transaction.set(practitionerActiveScansRef, { scans: updatedPractitionerScans }, { merge: true });
            } else {
              transaction.update(practitionerActiveScansRef, {
                scans: arrayRemove(practitionerScanToUpdate),
              });
            }
          }
        }
      }

      if (!scanToMove) {
        const updatedScans = activeScansData.scans.map((s) => {
          if (s.id === scanId) {
            return { ...s, scanStatus: newStatus, modifiedDate: timestamp, commentsToRead: s.commentsToRead || false, outgoingTracking: action === 'kitMailed' ? mailedData.outgoingUSPS || null : s.outgoingTracking || null };
          }
          return s;
        });
        transaction.set(activeScansRef, { scans: updatedScans }, { merge: true });

        const updatedPractitionerScans = practitionerActiveScansData.scans.map((s) => {
          if (s.id === scanId) {
            return { ...s, scanStatus: newStatus, modifiedDate: timestamp, commentsToRead: s.commentsToRead || false, outgoingTracking: action === 'kitMailed' ? mailedData.outgoingUSPS || null : s.outgoingTracking || null };
          }
          return s;
        });
        transaction.set(practitionerActiveScansRef, { scans: updatedPractitionerScans }, { merge: true });
      }

      updatedScan = {
        ...updatedScan,
        [action]: mailedData,
        dateModified: timestamp,
      };
    }
  });

  if (['kitMailed', 'reportsUploaded', 'imprintMailed', 'scanPaid', 'extraImprintCharged', 'cancel'].includes(action)) {
    try {
      const practitionerRef = doc(db, 'practitioners', practitionerId);
      const practitionerSnap = await getDoc(practitionerRef);
      if (!practitionerSnap.exists()) {
        throw new Error('Practitioner not found');
      }
      const practitionerData = practitionerSnap.data();
      const practitionerEmail = process.env.REACT_APP_ENVIRONMENT === 'PROD' ? practitionerData.email : 'renuhealthtech@gmail.com';
      const practitionerName = `${practitionerData.firstName} ${practitionerData.lastName}`;

      const clientSnap = await getDoc(clientRef);
      const clientData = clientSnap.data();
      const clientName = `${clientData.firstName} ${clientData.lastName}`;

      const scanDate = dayjs(scan.scanDate).format('MMMM D, YYYY');

      let emailData;

      // START Notify Practitioner via Email
      if (action === 'kitMailed') {
        emailData = {
          recipientEmail: practitionerEmail,
          recipientName: practitionerName,
          subject: "Client Kit Mailed",
          templateId: "scan-kit-mailed",
          dynamicTemplateData: {
            clientName,
            scanDate,
            outgoingTrackingNo: trackings?.outgoingUSPS || '',
            incomingTrackingNo: trackings?.returnUSPS || ''
          },
          emailType: "scan_kit_mailed",
          contextData: { scanId, clientId },
          userId: currentUser.uid,
          isTest: false
        };
      } else if (action === 'reportsUploaded') {
        emailData = {
          recipientEmail: practitionerEmail,
          recipientName: practitionerName,
          subject: "Client Reports Ready for Review",
          templateId: "scan-reports-ready",
          dynamicTemplateData: { clientName, scanDate },
          emailType: "scan_reports_ready",
          contextData: { scanId, clientId },
          userId: currentUser.uid,
          isTest: false
        };
      } else if (action === 'imprintMailed') {
        emailData = {
          recipientEmail: practitionerEmail,
          recipientName: practitionerName,
          subject: "Client Imprint Remedy Mailed",
          templateId: "scan-imprint-mailed",
          dynamicTemplateData: {
            clientName,
            scanDate,
            imprintTrackingNo: trackings?.imprintUSPS || ''
          },
          emailType: "scan_imprint_mailed",
          contextData: { scanId, clientId },
          userId: currentUser.uid,
          isTest: false
        };
      } else if (action === 'scanPaid') {
        emailData = {
          recipientEmail: practitionerEmail,
          recipientName: practitionerName,
          subject: "Scan Processed, Paid in Full",
          templateId: "scan-completed",
          dynamicTemplateData: { clientName, scanDate, amountCharged: amount },
          emailType: "scan_completed",
          contextData: { scanId, clientId, transId },
          userId: currentUser.uid,
          isTest: false
        };
      } else if (action === 'extraImprintCharged') {
        emailData = {
          recipientEmail: practitionerEmail,
          recipientName: practitionerName,
          subject: "Second Imprint Remedy Charged",
          templateId: "scan-second-remedy",
          dynamicTemplateData: { clientName, scanDate },
          emailType: "scan_second_imprint",
          contextData: { scanId, clientId },
          userId: currentUser.uid,
          isTest: false
        };
      } else if (action === 'cancel') {
        emailData = {
          recipientEmail: practitionerEmail,
          recipientName: practitionerName,
          subject: "Scan Cancelled",
          templateId: "scan-cancelled",
          dynamicTemplateData: { clientName, scanDate },
          emailType: "scan_cancelled",
          contextData: { scanId, clientId },
          userId: currentUser.uid,
          isTest: false
        };
      }

      await sendEmail(emailData);
      // END Notify Practitioner via Email
      // START Notify Client via Email
      if (action === 'kitMailed' || action === 'imprintMailed') {
        const clientEmail = process.env.REACT_APP_ENVIRONMENT === 'PROD' ? clientData.email : 'renuhealthtech@gmail.com';
        const clientName = `${clientData.firstName} ${clientData.lastName}`;

        if (action === 'kitMailed') {
          const upsURL = `https://www.ups.com/track?track=yes&trackNums=${trackings?.outgoingUSPS}`;
          const uspsURL = `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackings?.outgoingUSPS}`;

          emailData = {
            recipientEmail: clientEmail,
            recipientName: clientName,
            subject: "Your TruBalance Kit is on its way!",
            templateId: "client-kit-mailed",
            dynamicTemplateData: {
              practitionerName,
              clientName,
              scanDate,
              trackingNumber: trackings?.outgoingUSPS || '',
              trackingURL: trackings?.outgoingUSPS.startsWith('1Z') ? upsURL : uspsURL || ''
            },
            emailType: "client_kit_mailed",
            contextData: { scanId, clientId },
            userId: currentUser.uid,
            isTest: false
          };
        } else if (action === 'imprintMailed') {
          const upsURL = `https://www.ups.com/track?track=yes&trackNums=${trackings?.imprintUSPS}`;
          const uspsURL = `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackings?.imprintUSPS}`;

          emailData = {
            recipientEmail: clientEmail,
            recipientName: clientName,
            subject: "Your TruBalance Imprint Remedy has been mailed!",
            templateId: "client-imprint-mailed",
            dynamicTemplateData: {
              clientName,
              trackingNumber: trackings?.imprintUSPS || '',
              trackingURL: trackings?.imprintUSPS.startsWith('1Z') ? upsURL : uspsURL || ''
            },
            emailType: "client_imprint_mailed",
            contextData: { scanId, clientId },
            userId: currentUser.uid,
            isTest: false
          };
        }

        await sendEmail(emailData);
      }
    } catch (emailErr) {
      console.error(`Failed to send email for action ${action}:`, emailErr);
      // Do not throw to avoid reverting the transaction; log the error instead
    }
  }

  return updatedScan;
};